import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import ArrowRightIcon from '@mui/icons-material/ArrowRight';
import CancelIcon from '@mui/icons-material/Cancel';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import DoneIcon from '@mui/icons-material/Done';
import { Grid, Stack, Typography, Box, Link, styled, Tooltip } from '@mui/material';
import Divider from '@mui/material/Divider';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import { useEffect, useState, Fragment } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { amplitude } from 'wisestep-api';
import {
  WsDotColors,
  WsIconDoubleTick,
  WsTypography,
  WsTypoColors,
  WsTypoVariants,
  WsButton,
  WsPagination,
  Item,
  getDateUsingFormat,
  WsColors,
} from 'wisestep-base';

import { useWorkspaceContext } from '../../../context';
import useFetchEmailLogs from '../hooks/useEmailLogs';

export type IconProps = {
  statusColor: string;
};
interface StyledItemProp {
  selected: boolean;
}

const StyledItem = styled(Item)(({ selected }: StyledItemProp) => ({
  cursor: 'pointer',
  paddingBottom: 0,
  paddingTop: 30,
  backgroundColor: selected ? WsColors.TheBlues50 : 'transparent',
  '&:hover': {
    backgroundColor: WsColors.TheBlues50,
    borderColor: WsColors.TheBlues500,
  },
}));

export interface ContentData {
  to: string[];
  from: string;
  subject: string;
  message: string;
  fromName: string;
  recruiterId: string;
  recruiterName: string;
  sent_on: string;
  errors: string;
  isError: boolean;
}

interface ErrorDetails {
  value: string;
  url: string;
}

const ERROR_MESSAGE = {
  'Quota exceeded': { value: 'Your daily limit has been exceeded.' },
  'Illegal address': { value: "The candidate's email I'd is incorrect." },
  'Insufficient Permission': { value: 'Re-connect your account ', url: '/email-settings/' },
  'NO-REFRESH-TOKEN': { value: 'Re-connect your account ', url: '/email-settings/' },
  'EMPTY-FROM-EMAIL': { value: 'Sender email is not correct' },
  'Duplicate email': {
    value: 'An Email has been sent to this candidate in 1 minute  for a job',
  },
  ErrorMessageTransientError: {
    value: 'Try After Some time',
  },
  AuthenticationFailed: {
    value: 'Re-connect your account ',
    url: '/email-settings/',
  },
};
export const extractContent = (s: string) => {
  const span = document.createElement('span');
  if (s) span.innerHTML = s.replace(/<title>[.\d\w\s]*<\/title>/gi, '');
  return span.textContent || span.innerText;
};
const SentEmailLogs = () => {
  const [page, setPage] = useState<number>(0);
  const [filter, setFilter] = useState<string>('ALL');
  const [tempIndex, setTempIndex] = useState<number | null>(null);
  const [content, setContent] = useState<ContentData>({
    to: [''],
    from: '',
    subject: '',
    message: '',
    fromName: '',
    recruiterId: '',
    recruiterName: '',
    sent_on: '',
    errors: '',
    isError: false,
  });
  const result = useFetchEmailLogs(page, null, filter);
  const workspace = useWorkspaceContext();

  const setStatusIcon = (list) => {
    let status;
    if (list.isSent === true && list.isOpened === false) {
      status = <DoneIcon style={{ color: 'green' }} />;
    } else if (list.isSent === true && list.isOpened === true) {
      status = <WsIconDoubleTick color={WsDotColors.GREEN} />;
    } else if (list.isError === true) {
      status = (
        <Tooltip title={list.errors}>
          <CancelIcon style={{ color: 'red' }} />
        </Tooltip>
      );
    }
    return status;
  };

  const [sentEmailLogData, setSentEmailLogData] = useState<any>([]);
  useEffect(() => {
    const resultList = result.list.map((list) => ({
      from: list.from,
      isError: list.isError,
      isSent: list.isSent,
      isOpened: list.isOpened,
      to: list.to,
      subject: list.subject ?? '',
      fromName: list.fromName,
      sent_on: list.createdOn,
      status: setStatusIcon(list),
      body: list.message ?? '',
      recruiterId: list.recruiterId,
      recruiterName: list.recruiterName,
      errors: list.errors,
    }));
    setSentEmailLogData(resultList);
    if (resultList.length > 0) {
      const email = resultList[0];
      const selectedContent = {
        to: email?.to ?? [],
        from: email?.from ?? '',
        subject: email?.subject ?? '',
        message: email?.body ?? '',
        fromName: email?.fromName ?? '',
        recruiterId: email?.recruiterId ?? '',
        recruiterName: email?.recruiterName ?? '',
        sent_on: email?.sent_on ?? '',
        errors: email?.errors ?? '',
        isError: email.isError ?? false,
      };
      setContent(selectedContent);
      setTempIndex(0);
      setShowDrawer(true);
    }
  }, [result.list]);

  const [showDrawer, setShowDrawer] = useState(false);

  const onMailItemClick = (email: any, selectedIndex: number) => {
    setTempIndex(selectedIndex);
    const selectedContent = {
      to: email?.to,
      from: email?.from,
      subject: email?.subject ?? '',
      message: email?.body ?? '',
      fromName: email?.fromName,
      recruiterId: email?.recruiterId,
      recruiterName: email?.recruiterName,
      sent_on: email?.sent_on,
      errors: email?.errors,
      isError: email.isError,
    };
    setContent(selectedContent);
    setShowDrawer(true);
  };
  const emailAmplitudeEvent = (name: string, clickedOnName: string) =>
    amplitude(name, {
      clickedOn: clickedOnName,
      workspaceName: workspace.name,
      pageName: 'Mail',
      planType: workspace.planType,
      interfaceType: 'web',
      pageUrl: window.location.href,
    });
  const emailFilterLabel = {
    ['ALL']: () => emailAmplitudeEvent('Inbox_Filter_All', 'All'),
    ['SUCCESSFUL']: () => emailAmplitudeEvent('Inbox_Filter_successful', 'Successful'),
    ['FAILED']: () => emailAmplitudeEvent('Inbox_Filter_Failed', 'Failed'),
  };

  const performFilter = (action: string) => {
    emailFilterLabel[action]();
    setFilter(action);
  };

  const getErrorMessage = (errors: string): ErrorDetails => {
    const lastIndexOfMessage = errors.lastIndexOf('}');
    const message = errors.substring(errors.indexOf('{'), lastIndexOfMessage + 1);
    let error;
    if (lastIndexOfMessage > -1 && message) {
      const errorMessage = JSON.parse(message).message;
      if (errorMessage.includes('Quota exceed')) {
        error = ERROR_MESSAGE['Quota exceeded'];
      } else {
        error = ERROR_MESSAGE[errors] ?? { value: errorMessage };
      }
    } else if (errors.includes('ErrorMessageTransientError')) {
      error = ERROR_MESSAGE['ErrorMessageTransientError'];
    } else if (errors.includes('Authentication failed')) {
      error = ERROR_MESSAGE['AuthenticationFailed'];
    } else {
      error = ERROR_MESSAGE[errors];
    }
    return error;
  };

  return (
    <>
      <Grid container bgcolor="white" direction={'row'} justifyContent={'flex-start'} mb={1} p={1}>
        <WsButton variant="text" onClick={() => performFilter('ALL')}>
          All
        </WsButton>
        <WsButton variant="text" onClick={() => performFilter('SUCCESSFUL')}>
          Successful
        </WsButton>
        <WsButton variant="text" onClick={() => performFilter('FAILED')}>
          Failed
        </WsButton>
      </Grid>
      <Grid item bgcolor="grey" container xs={12} height={'40%'}>
        <Grid item xs={4} sx={{ overflowY: 'auto', height: '60%' }}>
          <List
            sx={{
              bgcolor: 'background.paper',
              borderColor: 'text.primary',
              pt: 0,
            }}
          >
            {sentEmailLogData?.length > 0 &&
              sentEmailLogData.map((email, index: number) => (
                <StyledItem
                  key={index}
                  selected={index === tempIndex}
                  onClick={() => {
                    onMailItemClick(email, index);
                  }}
                >
                  <ListItem sx={{ padding: 0, mb: 2 }} alignItems="flex-start">
                    <ListItemText
                      primary={
                        <Grid container xs={12} direction="row" justifyContent="space-between">
                          <Grid item xs={1.4}>
                            {email.status}
                          </Grid>
                          <Grid item xs={5.6}>
                            <Typography
                              noWrap
                              color={WsTypoColors.TERTIARY}
                              variant={WsTypoVariants.SUB_TITLE}
                            >
                              {email.recruiterName}
                            </Typography>
                          </Grid>
                          <Grid item xs={5}>
                            <Box sx={{ float: 'right' }}>
                              <Typography
                                noWrap
                                color={WsTypoColors.PRIMARY}
                                variant={WsTypoVariants.BODY1}
                              >
                                {getDateUsingFormat(email.sent_on, 'MMMM DD')}
                              </Typography>
                            </Box>
                          </Grid>
                        </Grid>
                      }
                      secondary={
                        <Fragment>
                          <Grid container xs={12} direction="row">
                            <Grid item xs={1}>
                              <ArrowRightIcon />
                            </Grid>
                            <Grid item xs={11}>
                              <Typography noWrap color={WsTypoColors.PRIMARY}>
                                {email.subject}
                              </Typography>
                            </Grid>
                            <Grid item xs={12}>
                              <Typography noWrap>
                                {`${extractContent(email?.body).toString().slice(0, 68)}  more... `}
                              </Typography>
                            </Grid>
                          </Grid>
                        </Fragment>
                      }
                    />
                  </ListItem>
                  <Divider />
                </StyledItem>
              ))}
            <Item sx={{ pr: 0 }}>
              {result.totalCount && result.totalCount > 20 && (
                <WsPagination
                  count={Math.ceil(result.totalCount / 20)}
                  onChange={(_, value) => setPage(value)}
                  itemsPerPage={20}
                  pageNumber={page}
                  defaultPage={page + 1}
                  isPageCount={true}
                  type="Candidates"
                  totalCount={result.totalCount}
                />
              )}
            </Item>
          </List>
        </Grid>
        {showDrawer && (
          <Grid
            container
            xs={8}
            sx={{ borderLeft: `1px solid ${WsColors.BorderGrey}`, height: '60%' }}
          >
            <Grid item xs={12} bgcolor="white" sx={{ borderColor: 'text.primary' }}>
              <Box sx={{ display: 'flex' }}>
                <Box
                  sx={{
                    margin: 1,
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                >
                  <AccountCircleIcon sx={{ fontSize: '4rem' }}></AccountCircleIcon>
                </Box>
                <Box sx={{ display: 'inline-block' }}>
                  <ToFormField content={content} label="" />
                  <FromLabel content={content} label={'To'} displayText={content.to.toString()} />
                  <FromLabel content={content} label={'From'} displayText={content.from} />
                  <FromLabel content={content} label={'Time'} displayText={content.sent_on} />
                </Box>
              </Box>
              <Grid item xs={12}>
                <Item>
                  {content.isError && content.errors && (
                    <Stack direction="row" spacing={1} justifyContent={'flex-start'}>
                      <CancelOutlinedIcon style={{ color: 'red' }} />
                      <ErrorInput error={getErrorMessage(content.errors)} />
                    </Stack>
                  )}
                  <FromLabel
                    label="Subject"
                    content={content}
                    displayText={content.subject}
                    labelColor={WsTypoColors.BLACK}
                  />
                  <Typography dangerouslySetInnerHTML={{ __html: content.message }} />
                </Item>
              </Grid>
            </Grid>
          </Grid>
        )}
      </Grid>
    </>
  );
};
export default SentEmailLogs;

interface contentDataType {
  label: string;
  content: ContentData;
  labelColor?: WsTypoColors;
  displayText?: string;
}

const FromLabel = ({
  label,
  labelColor = WsTypoColors.SENARY,
  displayText = '',
}: contentDataType) => (
  <Stack direction={'row'} justifyContent={'flex-start'} spacing={1} sx={{ pt: 0.5 }}>
    <WsTypography color={labelColor} variant={WsTypoVariants.BODY1}>
      {`${label}   :   ${displayText}`}
    </WsTypography>
  </Stack>
);

const ErrorInput = ({ error }: { error: ErrorDetails }) =>
  error?.url ? (
    <>
      <Stack direction="row" spacing={1}>
        <WsTypography
          color={WsTypoColors.SECONDARY}
          bold={false}
          variant={WsTypoVariants.SUB_TITLE}
        >
          {error?.value}
        </WsTypography>
        <Link component={RouterLink} to={error.url} underline="none">
          <WsTypography
            color={WsTypoColors.PRIMARY}
            variant={WsTypoVariants.SUB_TITLE}
            bold={true}
            cursor="pointer"
          >
            Connect now
          </WsTypography>
        </Link>
      </Stack>
    </>
  ) : (
    <WsTypography color={WsTypoColors.SECONDARY} bold={false} variant={WsTypoVariants.SUB_TITLE}>
      {error ? error.value : ''}
    </WsTypography>
  );

const ToFormField = ({ content }: contentDataType) => (
  <Stack direction={'row'} justifyContent={'flex-start'} sx={{ pt: 1, pb: 1 }}>
    <WsTypography color={WsTypoColors.PRIMARY} variant={WsTypoVariants.BODY1}>
      {content.recruiterName}
    </WsTypography>
    <WsTypography color={WsTypoColors.PRIMARY} variant={WsTypoVariants.BODY1}>
      (
    </WsTypography>
    <WsTypography color={WsTypoColors.PRIMARY} variant={WsTypoVariants.BODY1}>
      {content.recruiterId}
    </WsTypography>
    <WsTypography color={WsTypoColors.PRIMARY} variant={WsTypoVariants.BODY1}>
      )
    </WsTypography>
  </Stack>
);
